import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';
import {
  AlertDestination as GqlAlertDestination,
  TableAsset as GqlTableAsset,
  Test as GqlTest,
  AlertIntegrationType,
  CloudTestFeedbackType,
  CloudTestsSupported,
  LatestTestExecution,
  TestConfig,
} from '@elementary/common-ui/services/gql/types';
import {
  AccountEnvironmentBase,
  AgentCustomSqlCloudSchedule,
  AnomalyDirection,
  AnomalySensitivity,
  AssetDisplayType,
  DataContractConfigApi,
  DataContractFormValues,
  Period,
  SlimDwhConnection,
  Totals,
  ValueLabelOption,
  WarehouseType,
} from '@elementary/common-ui/types';
import { CadenceType } from 'shared/utils/cronUtils';

export type VolumeDetectionMode = 'anomaly_detection' | 'percentage_thresholds';

export type GroupTitleFormatters = Record<string, (value: string) => string>;

type BasePipelineTaskPerformanceConfig = { mode: 'anomaly' | 'threshold' };

type PipelineTaskPerformanceAnomalyConfig =
  BasePipelineTaskPerformanceConfig & {
    mode: 'anomaly';
    sensitivity?: AnomalySensitivity;
    trainingPeriod?: Period;
    anomalyDirection?: 'BOTH' | 'SPIKE' | 'DROP';
  };

type PipelineTaskPerformanceThresholdConfig =
  BasePipelineTaskPerformanceConfig & {
    mode: 'threshold';
    fixedThreshold?: Period;
  };

export type PipelineTaskPerformanceConfig =
  | PipelineTaskPerformanceAnomalyConfig
  | PipelineTaskPerformanceThresholdConfig;

export type PipelineTaskPerformanceConfigApi = {
  type: 'pipeline_task_performance';
  severity: number;
  owners: string[];
  tags: string[];
  config: {
    mode: 'anomaly' | 'threshold';
    sensitivity?: AnomalySensitivity | null;
    training_period?: Period | null;
    anomaly_direction?: string | null;
    fixed_threshold?: Period | null;
  };
};

export type PipelineTaskPerformanceSimulateApi = {
  type: 'pipeline_task_performance';
  config: PipelineTaskPerformanceConfigApi['config'];
};

export type AnomalySimulateApi = {
  type: 'anomaly_detection' | 'freshness_anomaly_detection';
  config: AutomatedTestAnomalyConfig;
};

export type SimulateTestResultApi =
  | AnomalySimulateApi
  | PipelineTaskPerformanceSimulateApi;

export type UserForm = {
  fullName: string;
};

export type UserUpdateForm = {
  userId: string;
  name: string | null;
  role_id: string | null;
};

export type UserInviteForm = {
  name: string;
  email: string;
  role_id: string;
};

export enum AUTH_TYPES {
  access_key = 'access_key',
  role = 'role',
}

type GlueMetaStoreForm = {
  id?: string;
  glueConnectionName: string;
  glueRegion: string;
} & (
  | {
      glueAuthMethod: AUTH_TYPES.access_key;
      glueAccessKeyId: string;
      glueSecretAccessKey: string;
      glueSessionToken?: string;
    }
  | {
      glueAuthMethod: AUTH_TYPES.role;
      glueRoleArn: string;
      glueExternalId?: string;
    }
);

export type DataCatalogMetaStore = 'GLUE' | 'BIGLAKE';

export type SetGlueMetaStoreApi = {
  name: string;
  config: {
    catalog_type: 'GLUE';
    region: string;
    auth:
      | {
          auth_type: AUTH_TYPES.role;
          role_arn: string;
          external_id?: string;
        }
      | {
          auth_type: AUTH_TYPES.access_key;
          access_key_id: string;
          secret_access_key: string;
          session_token?: string;
        };
  };
};

export type SetBigLakeMetaStoreApi = {
  name: string;
  config: {
    catalog_type: 'BIGLAKE';
    catalog_name: string;
    project?: string;
    catalog_location?: string;
  };
};

export type UpdateGlueMetaStoreApi = Partial<SetGlueMetaStoreApi>;

export type SetDataStoreApi = SetGlueMetaStoreApi | SetBigLakeMetaStoreApi;

export type FetchGlueMetaStoreApi = { id: string } & SetGlueMetaStoreApi;

export type FetchBigLakeMetaStoreApi = { id: string } & SetBigLakeMetaStoreApi;

export type FetchDataStoresApi = {
  integrations: (FetchGlueMetaStoreApi | FetchBigLakeMetaStoreApi)[];
};

type BigLakeMetaStoreForm = {
  id?: string;
  bigLakeConnectionName: string;
  bigLakeCatalogName: string;
  bigLakeProject?: string;
  bigLakeCatalogLocation?: string;
};

type GlueMetaStoreFormDiscriminated = {
  selectedMetaStore: 'GLUE';
} & GlueMetaStoreForm;

type BigLakeMetaStoreFormDiscriminated = {
  selectedMetaStore: 'BIGLAKE';
} & BigLakeMetaStoreForm;

export type DataCatalogForm =
  | GlueMetaStoreFormDiscriminated
  | BigLakeMetaStoreFormDiscriminated;

type EnvironmentBaseForm = {
  environmentName: string;
  environmentType?: EnvironmentType;
  warehouseType: WarehouseType;
  assetSources: AssetSourceFormData[];
};

export enum SnowflakeAuthMethod {
  PASSWORD = 'password',
  KEY_PAIR = 'key_pair',
}

type SnowflakeEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'snowflake';
  snowflakeAccount: string;
  snowflakeWarehouse: string;
  snowflakeUserRole?: string;
  snowflakeAuthMethod?: SnowflakeAuthMethod;
  snowflakeUser: string;
  snowflakePassword?: string | boolean;
  snowflakePrivateKey?: string | boolean;
  snowflakePrivateKeyPassphrase?: string;
};

type SshTunnelFormFields = {
  sshEnabled?: boolean;
  sshHost?: string;
  sshPort?: number;
  sshUsername?: string;
};

type RedshiftEnvironmentForm = EnvironmentBaseForm &
  SshTunnelFormFields & {
    warehouseType: 'redshift';
    redshiftHost: string;
    redshiftPort: number;
    redshiftUsername: string;
    redshiftPassword?: string | boolean;
  };

export enum BigQueryAuthMethod {
  SERVICE_ACCOUNT = 'service_account',
  WIF = 'wif',
}

type BigQueryEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'bigquery';
  bigqueryAuthMethod?: BigQueryAuthMethod;
  bigqueryAccountFile?: { content?: string; name: string };
  bigqueryWifFile?: { content?: string; name: string };
  bigqueryLocation: string;
};

type PostgresEnvironmentForm = EnvironmentBaseForm &
  SshTunnelFormFields & {
    warehouseType: 'postgres';
    postgresHost: string;
    postgresPort: string;
    postgresUser: string;
    postgresPassword?: string | boolean;
  };

export enum DatabricksAuthMethod {
  TOKEN = 'token',
  OAUTH = 'oauth',
}

export enum DatabricksStorageAccessType {
  ACCESS_KEYS = 'access_keys',
  ROLE_ARN = 'role_arn',
  CREDENTIALS_VENDING = 'credentials_vending',
}

export type DatabricksStorageAccess =
  | {
      storage_access_type: DatabricksStorageAccessType.ACCESS_KEYS;
      access_key_id: string;
      secret_access_key?: string | boolean;
      session_token?: string | boolean;
    }
  | {
      storage_access_type: DatabricksStorageAccessType.ROLE_ARN;
      role_arn: string;
      external_id?: string | boolean;
    }
  | {
      storage_access_type: DatabricksStorageAccessType.CREDENTIALS_VENDING;
    };

type DatabricksEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'databricks';
  databricksHost: string;
  databricksHttpPath: string;
  databricksAuthMethod: DatabricksAuthMethod;
  databricksAccessToken?: string | boolean;
  databricksClientId?: string;
  databricksClientSecret?: string | boolean;
  databricksStorageAccessType?: DatabricksStorageAccessType | null;
  databricksStorageAccessKeyId?: string;
  databricksStorageSecretAccessKey?: string | boolean;
  databricksStorageSessionToken?: string | boolean;
  databricksStorageRoleArn?: string;
  databricksStorageExternalId?: string | boolean;
};

export enum AthenaAuthMethod {
  SECRET_ACCESS_KEY = 'secret_access_key',
  ROLE_ARN = 'role_arn',
}

type AthenaEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'athena';
  athenaRegion: string;
  athenaWorkgroup?: string;
  athenaS3StagingDir: string;
  athenaAuthMethod?: AthenaAuthMethod;
  athenaAccessKeyId?: string;
  athenaSecretAccessKey?: string;
  athenaRoleArn?: string;
  athenaExternalId?: string;
  athenaSessionToken?: string;
};

type ClickhouseEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'clickhouse';
  clickhouseHost: string;
  clickhousePort: number;
  clickhouseUser: string;
  clickhousePassword: string | boolean;
};

type DremioEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'dremio';
  dremioHost: string;
  dremioProjectId: string;
  dremioUser: string;
  dremioToken: string;
};

type FabricEnvironmentForm = EnvironmentBaseForm & {
  warehouseType: 'fabric';
  fabricServer: string;
  fabricPort: number;
  fabricTenantId: string;
  fabricClientId: string;
  fabricClientSecret: string | boolean;
};

export type EnvironmentForm =
  | SnowflakeEnvironmentForm
  | RedshiftEnvironmentForm
  | BigQueryEnvironmentForm
  | PostgresEnvironmentForm
  | DatabricksEnvironmentForm
  | AthenaEnvironmentForm
  | ClickhouseEnvironmentForm
  | DremioEnvironmentForm
  | FabricEnvironmentForm;

export type SlackForm = {
  channelName: string;
};

export type MicrosoftTeamsWebhook = {
  name: string;
  url: string;
};

export type MicrosoftTeamsForm = {
  webhooks: MicrosoftTeamsWebhook[];
  defaultWebhookName: string;
};

export type OpsgenieIntegrationForm = {
  token: string | null;
  regionApiUrl: string;
};

export type VersionControl = 'GitHub' | 'GitLab' | 'Bitbucket' | 'AzureDevOps';
export type MessagingIntegration = 'Slack' | 'Teams' | 'TeamsBot';

export type ConnectedWorkspace = {
  tenantId: string;
  teamId: string;
};

export type IncidentManagementTool =
  | 'PagerDuty'
  | 'Opsgenie'
  | 'IncidentIo'
  | 'Rootly';

export type TicketingTool = 'jira' | 'linear' | 'servicenow';

export type BiTool =
  | 'Tableau'
  | 'Looker'
  | 'PowerBI'
  | 'Sigma'
  | 'ThoughtSpot'
  | 'Hex'
  | 'Lightdash';

export enum SigmaCloudProvider {
  AWS_US = 'AWS US',
  AWS_Canada = 'AWS Canada',
  AWS_Europe = 'AWS Europe',
  AWS_UK = 'AWS UK',
  Azure_US = 'Azure US',
  GCP = 'GCP',
}

type BiToolFormBase = {
  id: string;
  selectedBiTool: BiTool;
  enabled: boolean;
};

export type TableauForm = BiToolFormBase & {
  tableauConnectionName: string;
  selectedBiTool: 'Tableau';
  tableauPersonalAccessTokenName: string;
  tableauPersonalAccessTokenValue: string | boolean;
  tableauServerUrl: string;
  tableauSiteName: string;
};

export type LookerForm = BiToolFormBase & {
  lookerConnectionName: string;
  selectedBiTool: 'Looker';
  lookerServerUrl: string;
  lookerClientId: string;
  lookerClientSecret: string | boolean;
  lookerGitProvider: VersionControl | null;
  lookerGitToken: string | boolean;
  lookerRepoName: string;
  lookerEnterpriseUrl: string | null;
};

export type PowerBiForm = BiToolFormBase & {
  powerBiConnectionName: string;
  selectedBiTool: 'PowerBI';
  powerBiTenant: string;
  powerBiClientId: string;
  powerBiClientSecret: string | boolean;
  powerBiWorkspaces: string[];
};

export type SigmaForm = BiToolFormBase & {
  sigmaConnectionName: string;
  selectedBiTool: 'Sigma';
  sigmaCloudProvider: SigmaCloudProvider;
  sigmaClientId: string;
  sigmaClientSecret: string | boolean;
};

export type ThoughtSpotForm = BiToolFormBase & {
  thoughtSpotConnectionName: string;
  selectedBiTool: 'ThoughtSpot';
  thoughtSpotUsername: string;
  thoughtSpotSecretKey: string | boolean;
  thoughtSpotBaseUrl: string;
};

export type HexForm = BiToolFormBase & {
  hexConnectionName: string;
  selectedBiTool: 'Hex';
  hexWorkspaceToken: string | boolean;
  hexCustomUrl: string;
};

export type LightdashForm = BiToolFormBase & {
  lightdashConnectionName: string;
  selectedBiTool: 'Lightdash';
  lightdashHostUrl: string;
  lightdashApiToken: string | boolean;
  lightdashProjectUuids: string[];
};

export type BiToolForm =
  | TableauForm
  | LookerForm
  | PowerBiForm
  | SigmaForm
  | ThoughtSpotForm
  | HexForm
  | LightdashForm;

export type RepositoryForm = {
  gitProvider: VersionControl | null;
  repository: string;
  projectDir: string;
  branch: string;
  token?: string;
  orgUrl?: string;
  enterpriseUrl?: string;
};

export type TicketingForm = {
  projectId: string;
  type: TicketingTool;
  id?: string;
  name: string;
};

export type TokenForm = {
  token: string;
  orgUrl: string | null;
  enterpriseUrl: string | null;
};

export type OrgUrlForm = {
  orgUrl: string;
};

export type EnvironmentType = 'prod' | 'dev' | 'test';

type BaseConnectionCredentials = {
  type: WarehouseType;
};

type BigQueryConnectionCredentials = BaseConnectionCredentials & {
  type: 'bigquery';
  location: string;
  method: 'service-account-json' | 'external-account';
  keyfile?: string;
  keyfile_json?: GoogleCloudKeyFileJson;
  external_account_json?: ExternalAccountCredentialJson;
  external_account_file?: string;
};

type ClickhouseConnectionCredentials = BaseConnectionCredentials & {
  type: 'clickhouse';
  host: string;
  port: number;
  user: string;
  password: string;
};

type RedshiftConnectionCredentials = BaseConnectionCredentials & {
  type: 'redshift';
  host: string;
  user: string;
  password: string;
  port: number;
};

type DatabricksConnectionCredentialsBase = BaseConnectionCredentials & {
  type: 'databricks';
  host: string;
  http_path: string;
  auth_type: DatabricksAuthMethod;
  storage_access?: DatabricksStorageAccess | null;
};

export type DatabricksTokenConnectionCredentials =
  DatabricksConnectionCredentialsBase & {
    auth_type: DatabricksAuthMethod.TOKEN;
    token: string;
    client_id?: never;
    client_secret?: never;
  };

export type DatabricksOAuthConnectionCredentials =
  DatabricksConnectionCredentialsBase & {
    auth_type: DatabricksAuthMethod.OAUTH;
    client_id: string;
    client_secret: string;
    token?: never;
  };

type DatabricksConnectionCredentials =
  | DatabricksTokenConnectionCredentials
  | DatabricksOAuthConnectionCredentials;

type PostgresConnectionCredentials = BaseConnectionCredentials & {
  type: 'postgres';
  host: string;
  port: string;
  user: string;
  password: string;
};

type SnowflakeConnectionCredentialsBase = BaseConnectionCredentials & {
  type: 'snowflake';
  account: string;
  warehouse: string;
  user: string;
  role?: string;
};

type DremioConnectionCredentials = BaseConnectionCredentials & {
  type: 'dremio';
  host: 'api.dremio.cloud' | 'api.eu.dremio.cloud';
  project_id: string;
  user: string;
  token: string;
};

type FabricConnectionCredentials = BaseConnectionCredentials & {
  type: 'fabric';
  server: string;
  port: number;
  tenant_id: string;
  client_id: string;
  client_secret: string;
};

export type SnowflakePasswordConnectionCredentials =
  SnowflakeConnectionCredentialsBase & {
    password: string;
  };

export type SnowflakeKeyPairConnectionCredentials =
  SnowflakeConnectionCredentialsBase & {
    private_key: string;
    private_key_passphrase?: string;
  };

type SnowflakeConnectionCredentials =
  | SnowflakePasswordConnectionCredentials
  | SnowflakeKeyPairConnectionCredentials;

type AthenaConnectionCredentialsBase = BaseConnectionCredentials & {
  type: 'athena';
  region: string;
  workgroup: string;
  s3_staging_dir: string;
};

export type AthenaSecretAccessKeyConnectionCredentials =
  AthenaConnectionCredentialsBase & {
    aws_access_key_id: string;
    aws_secret_access_key: string;
  };

export type AthenaRoleArnConnectionCredentials =
  AthenaConnectionCredentialsBase & {
    aws_role_arn: string;
    aws_external_id?: string;
  };

type AthenaConnectionCredentials =
  | AthenaSecretAccessKeyConnectionCredentials
  | AthenaRoleArnConnectionCredentials;

export type DwhConnectionCredentials =
  | SnowflakeConnectionCredentials
  | RedshiftConnectionCredentials
  | BigQueryConnectionCredentials
  | DatabricksConnectionCredentials
  | PostgresConnectionCredentials
  | AthenaConnectionCredentials
  | ClickhouseConnectionCredentials
  | DremioConnectionCredentials
  | FabricConnectionCredentials;

export type SshTunnelConfig = {
  host: string;
  port: number;
  username: string;
};

export type DwhConnection = SlimDwhConnection & {
  credentials: DwhConnectionCredentials;
  ssh?: SshTunnelConfig | null;
};

export type FetchEnvironmentApi = AccountEnvironmentBase & {
  dwhConnection: DwhConnection;
  assetSources: AssetSourceApi[];
};

export type GoogleCloudKeyFileJson = {
  type: 'service_account';
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
};

export type ExternalAccountCredentialJson = {
  type: 'external_account';
  audience: string;
  subject_token_type: string;
  token_url: string;
  credential_source: Record<string, unknown>;
  service_account_impersonation_url?: string;
  token_info_url?: string;
  quota_project_id?: string;
  universe_domain?: string;
};

export type DbtAssetSourceFormData = {
  id?: string;
  type: 'dbt';
  name: string;
  schema: string;
  database: string | undefined;
};

export type WarehouseDbFilterFormData = {
  database: string;
  schemas: string[];
};

export type WarehouseAssetSourceFormData = {
  id?: string;
  type: 'warehouse';
  name: string;
  dbFilters: WarehouseDbFilterFormData[];
};

export type AssetSourceFormData =
  | DbtAssetSourceFormData
  | WarehouseAssetSourceFormData;

export type DbtAssetSourceApi = {
  type: 'dbt';
  id: string;
  name: string;
  schema: string;
  database: string | null;
};

export type WarehouseDbFiltersApi = {
  database: string;
  schemas: string[] | null;
};

export type WarehouseAssetSourceApi = {
  type: 'warehouse';
  id: string;
  name: string;
  filters: {
    dbFilters: WarehouseDbFiltersApi[];
  };
};

export type AssetSourceApi = DbtAssetSourceApi | WarehouseAssetSourceApi;

export type CreateDbtAssetSourceApi = {
  type: 'dbt';
  name: string;
  schema: string;
  database?: string;
};

export type CreateWarehouseAssetSourceApi = {
  type: 'warehouse';
  name: string;
  filters: {
    dbFilters: WarehouseDbFiltersApi[];
  };
};

export type CreateAssetSourceApi =
  | CreateDbtAssetSourceApi
  | CreateWarehouseAssetSourceApi;

export type UpdateDbtAssetSourceApi = {
  type: 'dbt';
  id: string;
  name?: string;
};

export type UpdateWarehouseAssetSourceApi = {
  type: 'warehouse';
  id: string;
  name?: string;
  filters?: {
    dbFilters: WarehouseDbFiltersApi[];
  };
};

export type UpdateAssetSourceApi =
  | UpdateDbtAssetSourceApi
  | UpdateWarehouseAssetSourceApi;

export type DeleteAssetSourceApi = {
  id: string;
};

export type AssetSourcesUpdateApi = {
  add?: CreateAssetSourceApi[];
  update?: UpdateAssetSourceApi[];
  delete?: DeleteAssetSourceApi[];
};

export type CreateDwhConnectionApi = {
  warehouseType: WarehouseType;
  credentials: DwhConnectionCredentials;
  ssh?: SshTunnelConfig | null;
};

export type UpdateDwhConnectionApi = {
  warehouseType?: WarehouseType;
  credentials?: Partial<DwhConnectionCredentials>;
  ssh?: SshTunnelConfig | null;
};

export type CreateEnvironmentApiRequest = {
  name: string;
  environmentType?: EnvironmentType;
  dwhConnection: CreateDwhConnectionApi;
  assetSources: CreateAssetSourceApi[];
};

export type UpdateEnvironmentApiRequest = {
  name?: string;
  environmentType?: EnvironmentType;
  dwhConnection?: UpdateDwhConnectionApi;
  assetSources?: AssetSourcesUpdateApi;
};

export type CreateEnvironmentApiResponse = {
  id: string;
};

type DefaultDestinationType = {
  channelName: string;
};

export type FetchSlackApi = {
  integration?: 'oauth' | 'token';
  defaultDestination?: DefaultDestinationType;
  teamName?: string | null;
};

export type SetSlackIntegrationResponse = {
  workspaceChanged: boolean;
  newTeamName?: string | null;
};

export type SetSlackApi = {
  channelName: string;
};

export type SendSlackTestMessageApi = {
  channel_name: string;
  message?: string;
};

export type SendSlackTestMessageResponseApi = {
  success: boolean;
  message: string;
};

export type FetchMicrosoftTeamsWebhooksApi = {
  webhooks: MicrosoftTeamsWebhook[];
};

export type FetchMicrosoftTeamsDefaultsApi = {
  defaultDestination?: {
    defaultWebhookName: string;
  };
};
export type SetMicrosoftTeamsWebhooksApi = {
  webhooks: MicrosoftTeamsWebhook[];
};

export type SetMicrosoftTeamsDefaultsApi = {
  defaultWebhookName: string;
};

export type SendTeamsTestMessageApi = {
  message?: string;
  webhook_name: string;
  webhook_url: string;
};

export type SendTeamsTestMessageResponseApi = {
  success: boolean;
  message: string;
};

export type SendOpsgenieTestIncidentApi = {
  message?: string;
  token?: string | null;
  regionApiUrl?: string | null;
};

export type SendOpsgenieTestIncidentResponseApi = {
  success: boolean;
  message: string;
};

export type SendPagerDutyTestIncidentApi = {
  service_id: string;
  message?: string;
};

export type SendPagerDutyTestIncidentResponseApi = {
  success: boolean;
  message: string;
};

export type FetchPagerDutyApi = {
  integrated: boolean;
};

export type FetchOpsgenieApi = {
  integrated: boolean;
  regionApiUrl: string | null;
};

export type SetOpsgenieApi = {
  token: string | null;
  regionApiUrl: string | null;
};

export type UpdateOpsgenieApi = {
  token: string | null;
  regionApiUrl: string | null;
};

export type IncidentIoAlertSource = {
  name: string;
  alertSourceConfigId: string;
  token: string;
};

export type IncidentIoIntegrationForm = {
  sources: IncidentIoAlertSource[];
};

export type FetchIncidentIoApi = {
  integrated: boolean;
  sources?: { name: string; alertSourceConfigId: string }[] | null;
};

export type SetIncidentIoApi = {
  sources: IncidentIoAlertSource[];
};

export type UpdateIncidentIoApi = {
  sources: IncidentIoAlertSource[];
};

export type SendIncidentIoTestIncidentApi = {
  message?: string;
  sourceName?: string | null;
  token?: string | null;
  alertSourceConfigId?: string | null;
};

export type SendIncidentIoTestIncidentResponseApi = {
  success: boolean;
  message: string;
};

export type RootlyAlertSource = {
  name: string;
  webhookEndpoint: string;
  secret: string;
};

export type RootlyIntegrationForm = {
  sources: RootlyAlertSource[];
};

export type FetchRootlyApi = {
  integrated: boolean;
  sources?: { name: string; webhookEndpoint: string }[] | null;
};

export type SetRootlyApi = {
  sources: RootlyAlertSource[];
};

export type UpdateRootlyApi = {
  sources: RootlyAlertSource[];
};

export type SendRootlyTestIncidentApi = {
  message?: string;
  sourceName?: string | null;
  webhookEndpoint?: string | null;
  secret?: string | null;
};

export type SendRootlyTestIncidentResponseApi = {
  success: boolean;
  message: string;
};

export type TableauSettingsApi = {
  personal_access_token_name: string;
  personal_access_token_value: boolean;
  server_url: string;
  site_name: string | null;
};
export type LookerSettingsApi = {
  server_url: string;
  client_id: string;
  client_secret: string;
  git_provider: VersionControl;
  git_token: boolean;
  repo_name: string;
};

export type PowerBiSettingsApi = {
  tenant: string;
  client_id: string;
  client_secret: boolean;
  workspaces: string[];
};

export type SigmaSettingsApi = {
  client_id: string;
  client_secret: boolean;
  cloud_provider: SigmaCloudProvider;
};

export type ThoughtSpotSettingsApi = {
  username: string;
  secret_key: string | boolean;
  base_url: string;
};

export type HexSettingsApi = {
  workspace_token: string | boolean;
  custom_url: string | null;
};

export type LightdashSettingsApi = {
  host_url?: string;
  api_token: string | boolean;
  project_uuids?: string[];
};

export type FetchBiIntegrationApi = {
  id: string;
  name: string;
  bi_type: BiTool;
  enabled: boolean;
  status: 'connected' | 'error';
  last_sync_status: 'success' | 'error' | 'running' | null;
  last_sync_error_message: string | null;
  last_synced_at: string | null;
  bi_settings:
    | TableauSettingsApi
    | LookerSettingsApi
    | SigmaSettingsApi
    | PowerBiSettingsApi
    | ThoughtSpotSettingsApi
    | HexSettingsApi
    | LightdashSettingsApi;
};

export type FetchBiIntegrationsApi = {
  destinations: FetchBiIntegrationApi[];
};

export type SetBiIntegrationApi = {
  name: string;
  bi_type: BiTool;
  enabled: boolean;
  bi_settings: Partial<
    | TableauSettingsApi
    | LookerSettingsApi
    | SigmaSettingsApi
    | PowerBiSettingsApi
    | ThoughtSpotSettingsApi
    | HexSettingsApi
    | LightdashSettingsApi
  >;
};

export type FetchNangoApi = {
  host: string;
  connectionId: string;
  publicKey: string;
  hmac: string;
};

export type FetchNangoInstalledAppsApi = {
  slack: boolean;
  github: boolean;
  gitlab: boolean;
  pagerduty: boolean;
  microsoft_entra: boolean;
};

export type CodeProjectItem = {
  repository: string;
  project_dir: string | null;
  branch: string | null;
  dbt_project_id: string;
};

export type CodeProjectAdditionalSettings = {
  org_url?: string | null;
  enterprise_url?: string | null;
  add_labels?: boolean | null;
  branch_prefix?: string | null;
  commit_message_prefix?: string | null;
  require_ticket_id?: boolean | null;
};

export type CreateCodeProjectRequest = {
  code_project_type: string;
  token: string | null;
  additional_settings: CodeProjectAdditionalSettings | null;
  code_projects: CodeProjectItem[];
};

export type UpdateCodeProjectRequest = {
  token?: string | null;
  additional_settings?: CodeProjectAdditionalSettings | null;
  code_projects?: CodeProjectItem[];
};

export type FetchCodeProjectsResponse = {
  code_project_type: string;
  additional_settings: CodeProjectAdditionalSettings | null;
  code_projects: CodeProjectItem[];
};

export type TicketingIntegration = {
  id: string;
  name: string;
  enabled: boolean;
  project_id: string;
  type: TicketingTool;
};

export type FetchTicketingApi = {
  integrations: TicketingIntegration[];
};

export type JiraWebhookConfig = {
  webhook_url_path: string;
  has_secret: boolean;
  webhook_configured: boolean;
};

export type GenerateJiraWebhookSecretResponse = {
  secret: string;
  webhook_url_path: string;
};

export type TicketingProject = {
  name: string;
  key: string;
  id: string;
};

export type FetchTicketingProjectsApi = {
  projects: TicketingProject[];
};

export type ActivityLogsIntegrationType = 'datadog' | 'splunk' | 'gcs' | 's3';

type IntegrationProperties = {
  integration_type: ActivityLogsIntegrationType;
};

export type DatadogActivityLogsIntegrationProperties = IntegrationProperties & {
  integration_type: 'datadog';
  api_key: string | boolean;
  site: string;
  source: string;
  service: string;
  tags?: Record<string, string> | null;
};

export type SplunkActivityLogsIntegrationProperties = IntegrationProperties & {
  integration_type: 'splunk';
  hec_url: string;
  token: string | boolean;
  index?: string | null;
};

export type GcsActivityLogsIntegrationProperties = IntegrationProperties & {
  integration_type: 'gcs';
  bucket_name: string;
  location?: string | null;
  keyfile?: string;
  keyfile_json?: GoogleCloudKeyFileJson | boolean;
  external_account_json?: ExternalAccountCredentialJson | boolean;
  external_account_file?: string;
  max_file_size?: string | null;
};

export type S3AuthMethod = 'role' | 'access_key';

export type S3RoleAuth = {
  auth_type: 'role';
  role_arn: string;
  external_id?: string | null;
};

export type S3AccessKeyAuth = {
  auth_type: 'access_key';
  access_key_id: string;
  secret_access_key: string | boolean;
};

export type S3ActivityLogsIntegrationProperties = IntegrationProperties & {
  integration_type: 's3';
  bucket_name: string;
  region: string;
  auth: S3RoleAuth | S3AccessKeyAuth;
  use_privatelink?: boolean;
  max_file_size?: string | null;
};

export type ActivityLogsIntegrationProperties =
  | DatadogActivityLogsIntegrationProperties
  | SplunkActivityLogsIntegrationProperties
  | GcsActivityLogsIntegrationProperties
  | S3ActivityLogsIntegrationProperties;

export type ActivityLogsIntegrationScopeType =
  | 'ALL'
  | 'ACCOUNT_ONLY'
  | 'ENV_SPECIFIC';

export type ActivityLogsIntegrationScope =
  | { type: 'ALL' }
  | { type: 'ACCOUNT_ONLY' }
  | { type: 'ENV_SPECIFIC'; env_id: string };

export type ActivityLogsIntegration = {
  id: string;
  name: string;
  enabled: boolean;
  type: ActivityLogsIntegrationType;
  scope: ActivityLogsIntegrationScope;
  properties: ActivityLogsIntegrationProperties | null;
};

export type FetchActivityLogsIntegrationsApi = {
  integrations: ActivityLogsIntegration[];
};

export type DatadogActivityLogsIntegrationPropertiesApi = {
  integration_type: 'datadog';
  api_key: string;
  site: string;
  source: string;
  service: string;
  tags?: Record<string, string> | null;
};

export type SplunkActivityLogsIntegrationPropertiesApi = {
  integration_type: 'splunk';
  hec_url: string;
  token: string;
  index?: string | null;
};

export type GcsActivityLogsIntegrationPropertiesApi = {
  integration_type: 'gcs';
  bucket_name: string;
  location?: string | null;
  keyfile?: string;
  keyfile_json?: GoogleCloudKeyFileJson;
  external_account_json?: ExternalAccountCredentialJson;
  external_account_file?: string;
  max_file_size?: string | null;
};

export type S3RoleAuthApi = {
  auth_type: 'role';
  role_arn: string;
  external_id?: string | null;
};

export type S3AccessKeyAuthApi = {
  auth_type: 'access_key';
  access_key_id: string;
  secret_access_key: string;
};

export type S3ActivityLogsIntegrationPropertiesApi = {
  integration_type: 's3';
  bucket_name: string;
  region: string;
  auth: S3RoleAuthApi | S3AccessKeyAuthApi;
  use_privatelink?: boolean;
  max_file_size?: string | null;
};

export type CreateActivityLogsIntegrationApi = {
  name: string;
  enabled: boolean;
  scope: ActivityLogsIntegrationScope;
  properties:
    | DatadogActivityLogsIntegrationPropertiesApi
    | SplunkActivityLogsIntegrationPropertiesApi
    | GcsActivityLogsIntegrationPropertiesApi
    | S3ActivityLogsIntegrationPropertiesApi;
};

export type UpdateDatadogActivityLogsIntegrationPropertiesApi = Partial<
  Omit<DatadogActivityLogsIntegrationPropertiesApi, 'integration_type'>
> & {
  integration_type: 'datadog';
};

export type UpdateSplunkActivityLogsIntegrationPropertiesApi = Partial<
  Omit<SplunkActivityLogsIntegrationPropertiesApi, 'integration_type' | 'token'>
> & {
  integration_type: 'splunk';
  token?: string;
};

export type UpdateGcsActivityLogsIntegrationPropertiesApi = Partial<
  Omit<
    GcsActivityLogsIntegrationPropertiesApi,
    'integration_type' | 'keyfile_json' | 'external_account_json'
  >
> & {
  integration_type: 'gcs';
  keyfile_json?: GoogleCloudKeyFileJson;
  external_account_json?: ExternalAccountCredentialJson;
};

export type UpdateS3RoleAuthApi = Partial<Omit<S3RoleAuthApi, 'auth_type'>> & {
  auth_type: 'role';
};

export type UpdateS3AccessKeyAuthApi = Partial<
  Omit<S3AccessKeyAuthApi, 'auth_type' | 'secret_access_key'>
> & {
  auth_type: 'access_key';
  secret_access_key?: string;
};

export type UpdateS3ActivityLogsIntegrationPropertiesApi = Partial<
  Omit<S3ActivityLogsIntegrationPropertiesApi, 'integration_type' | 'auth'>
> & {
  integration_type: 's3';
  auth?: UpdateS3RoleAuthApi | UpdateS3AccessKeyAuthApi;
};

export type UpdateActivityLogsIntegrationApi = Partial<{
  name: string;
  enabled: boolean;
  scope: ActivityLogsIntegrationScope;
  properties:
    | UpdateDatadogActivityLogsIntegrationPropertiesApi
    | UpdateSplunkActivityLogsIntegrationPropertiesApi
    | UpdateGcsActivityLogsIntegrationPropertiesApi
    | UpdateS3ActivityLogsIntegrationPropertiesApi;
}>;

export type CreateTicketingApi = {
  enabled: boolean;
  project_id: string;
  type: TicketingTool;
  name: string;
};

export type UpdateTicketingApi = {
  enabled: boolean;
  project_id: string;
  name: string;
};

export type TestGitAccessTokenApi = {
  code_project_type: string;
  token: string;
  additional_auth_settings?: {
    org_url?: string | null;
    enterprise_url?: string | null;
  };
};

export type Permission =
  | 'dashboard'
  | 'test_results'
  | 'test_runs'
  | 'model_runs'
  | 'lineage'
  | 'slack'
  | 'code_project'
  | 'bi'
  | 'test_management'
  | 'metastore'
  | 'fivetran_integration';

export type FlagsApi = {
  [key in Permission]: boolean;
};

export type ConfigurationType = 'CLOUD' | 'CODE' | 'CUSTOM';

export type AlertDestination = {
  rule_id?: string;
  channel: string;
  type?: AlertIntegrationType;
  configuration_type?: ConfigurationType;
  id?: string;
};

export type Test = {
  id: string;
  asset_id?: string;
  schema: string;
  table: string;
  column?: string;
  package: 'elementary' | 'dbt';
  name: string;
  type?: string;
  source_type?: string;
  severity: string;
  owners: string[];
  model_owners?: string[];
  tags: string[];
  model_tags?: string[];
  args: object;
  updated_at: string;
  updated_by?: string;
  model_name?: string;
  source_name?: string;
  meta: object;
  description?: string;
  is_singular: boolean;
  is_custom_sql: boolean;
  original_path: string;
  is_cloud?: boolean;
  channels: string[];
  alert_destinations?: AlertDestination[];
  alert_destinations_type?: ConfigurationType;
  muted?: boolean;
  database_name?: string;
  last_data_modification_time?: string;
  materialization?: string;
};

export type Resource = {
  id: string;
  name: string;
  display_name?: string;
  schema: string;
  table_name?: string;
  tags: string[];
  owners: string[];
  source_name?: string;
  db_name?: string;
  materialization?: string;
  columns: Array<{
    name: string;
    type: string;
  }>;
  table_type?: string;
  source_type?: string;
  fqn?: string;
  cloud_tests_supported?: Partial<
    Record<
      | 'freshness_anomalies'
      | 'volume_anomalies'
      | 'data_contract'
      | 'pipeline_task_performance',
      boolean
    >
  >;
};

export type ColumnResource = {
  name: string;
  display_name?: string;
  type: string;
  id: string;
  asset_id: string;
  schema: string;
  model_id: string;
  model_name: string;
  source_name?: string;
  tags: string[];
  owners: string[];
  table_type?: string;
  source_type?: string;
  table_fqn?: string;
  db_name?: string;
  table_name?: string;
};

export type QualityDimension =
  | 'completeness'
  | 'uniqueness'
  | 'freshness'
  | 'validity'
  | 'accuracy'
  | 'consistency';

export type ConditionalOperator = '>' | '<' | '=' | '>=' | '<=' | '!=';

export type Severity = 'error' | 'warn';

export type SeverityFormField = {
  type?: Severity;
  conditionalWarning?: { operator?: ConditionalOperator; value?: number };
  conditionalError?: { operator?: ConditionalOperator; value?: number };
};

export const CLOUD_TEST_TYPES = [
  'cloudFreshnessAnomalies',
  'cloudVolumeAnomalies',
  'cloudDataContract',
  'cloudPipelineTaskPerformance',
] as const;

export type CloudTestType = typeof CLOUD_TEST_TYPES[number];

export const CLOUD_SQL_TEST_TYPES = [
  'cloudSqlUniqueness',
  'cloudSqlCompleteness',
  'cloudSqlValidity',
  'cloudSqlRelationship',
] as const;

export type AddTestForm = {
  resources: string[];
  resourceType: 'tables' | 'columns';
  testType:
    | 'freshnessAnomalies'
    | 'cloudFreshnessAnomalies'
    | 'eventFreshnessAnomalies'
    | 'volumeAnomalies'
    | 'cloudVolumeAnomalies'
    | 'dimensionAnomalies'
    | 'schemaChanges'
    | 'cloudDataContract'
    | 'cloudPipelineTaskPerformance'
    | 'cloudSqlUniqueness'
    | 'cloudSqlCompleteness'
    | 'cloudSqlValidity'
    | 'cloudSqlRelationship'
    | 'unique'
    | 'notNull'
    | 'acceptedValues'
    | 'relationship'
    | 'customQuery'
    | 'yamlTest'
    | '';
  timestampColumn: string;
  updatedAtTimestampColumn: string;
  columnGroup: string[];
  detectionPeriod: number;
  detectionUnit: 'day' | 'hour' | 'week' | 'month';
  trainingPeriodCount: number;
  trainingPeriodUnit: 'day' | 'week' | 'month';
  detectionEventType: 'anomaly' | 'drop' | 'spike';
  slaWarning: boolean;
  slaError: boolean;
  slaWarningDetectionPeriod: number;
  slaErrorDetectionPeriod: number;
  slaWarningDetectionUnit: 'day' | 'hour' | 'minute';
  slaErrorDetectionUnit: 'day' | 'hour' | 'minute';
  severity: SeverityFormField;
  whereClause: string;
  owners: string[];
  tags: string[];
  qualityDimension?: QualityDimension;
  description: string;
  sensitivity: number;
  seasonality: string;
  backfillDays: number;
  values: string[];
  valuesAreNotStrings: boolean;
  columnName: string;
  tableName: string;
  customQuery: string;
  customQueryConverted: string;
  customQueryCodeType?: ConvertQueryApi['code_type'];
  customQueryDestination?: CustomQueryDestination;
  scheduleType?: CustomSqlCloudTestSchedule['type'];
  intervalHours?: number;
  scheduleHour?: number;
  scheduleMinute?: number;
  scheduleDayOfWeek?: number;
  scheduleCron?: string;
  customSqlPrimaryAssetId?: string | null;
  customSqlReferencedAssets?: CustomSqlReferencedAsset[];
  customSqlRequiresAssetSelection?: boolean;
  customSqlValidated?: boolean;
  storeRowSample?: boolean;
  sampleRowLimit?: number;
  queryTimeoutSeconds?: number;
  testName: string;
  path: string;
  testedTable: string;
  yaml: string;
  yamlValid?: boolean;
  yamlTemplateName?: string;
  testNameOverride?: string;
  limit?: number;
  shouldStoreFailures?: boolean;
  anomalyConfig?: {
    fixedThreshold: null | Period;
    sensitivity?: AnomalySensitivity;
    detectionPeriod?: Period;
    anomalyDirection?: 'BOTH' | 'DROP' | 'SPIKE';
    trainingPeriod?: Period | null;
    spikeThreshold?: number | null;
    dropThreshold?: number | null;
    failureThresholdGrace?: Period | null;
  };
  volumeDetectionMode?: VolumeDetectionMode;
  pipelineTaskPerformanceConfig?: PipelineTaskPerformanceConfig;
  baselineMode?: DataContractFormValues['baselineMode'];
  baselineColumns?: DataContractFormValues['baselineColumns'];
  enforcementLevel?: DataContractFormValues['enforcementLevel'];
  ticketId?: string | null;
};

export type TimestampFormField = Extract<
  keyof AddTestForm,
  'timestampColumn' | 'updatedAtTimestampColumn'
>;

export type PullRequest = {
  id: string;
  url: string;
  status: string;
  title?: string;
  created_at: string;
  created_by: string;
  name?: string;
  pr_id?: string;
  pr_type?: string;
  error_message?: string;
};

export type FetchPullRequestsApi = {
  pull_requests: Array<PullRequest>;
};

export type FetchDwhApi = {
  warehouse_type:
    | 'postgres'
    | 'redshift'
    | 'snowflake'
    | 'bigquery'
    | 'databricks'
    | 'clickhouse'
    | 'fabric';
  database: string;
};

export type DeleteTestApi = Array<{
  test_id: string;
  resource_name: string;
  source_name?: string;
  column?: string;
  package: 'elementary' | 'dbt';
  name: string;
  args?: object;
  owners: string[];
  tags: string[];
  meta: object;
}>;

export type DeleteSingularTestApi = {
  test_ids: string[];
};

export type AddTestBaseApiItem = {
  name: string;
  owners?: string[];
  description?: string;
  tags?: string[];
  severity?: 'error' | 'warn' | 'conditional';
  quality_dimension?: QualityDimension;
};

export type AddTestApiItem = AddTestBaseApiItem & {
  asset_id: string;
  resource_name: string;
  source_name?: string;
  column?: string;
  package?: 'elementary' | 'dbt';
  args: object;
  subscribers?: string[];
  values?: string[];
  quote?: boolean;
  field?: string;
  warn_if?: string;
  error_if?: string;
  to?: string;
};

export type AddYamlTestApiItem = AddTestBaseApiItem & {
  asset_id: string;
  resource_name: string;
  source_name?: string;
  column?: string;
  yaml: string;
  warn_if?: string;
  error_if?: string;
  where?: string;
  limit?: number;
  store_failures?: boolean;
};

export type AutomatedTestAnomalyConfig = {
  sensitivity?: AnomalySensitivity;
  anomaly_direction?: 'BOTH' | 'DROP' | 'SPIKE';
  detection_period?: Period | null;
  fixed_threshold?: Period | null;
};

export type AnomalyDetectionTestConfig = {
  type: 'anomaly_detection';
  anomaly_config: {
    anomaly_direction: AnomalyDirection;
    detection_period: Period;
    sensitivity: AnomalySensitivity;
    training_period?: Period;
    excluded_time_ranges?: Array<{
      start_time: string;
      end_time: string;
    }>;
    spike_threshold?: number | null;
    drop_threshold?: number | null;
  };
  severity: number;
  owners: string[];
  tags: string[];
  muted: boolean;
};

export type FreshnessAnomalyDetectionTestConfig = {
  type: 'freshness_anomaly_detection';
  anomaly_config: {
    sensitivity: AnomalySensitivity;
    fixed_threshold: Period | null;
    training_period?: Period;
    failure_threshold_grace?: Period | null;
  };
  severity: string;
  owners: string[];
  tags: string[];
  muted: boolean;
};

export type CloudAnomalyTestConfig =
  | AnomalyDetectionTestConfig
  | FreshnessAnomalyDetectionTestConfig;

export type PipelineTaskPerformanceCloudTestConfig = {
  mode: 'anomaly' | 'threshold';
  sensitivity?: AnomalySensitivity;
  anomaly_direction?: string;
  training_period?: Period;
  fixed_threshold?: Period;
};

export type AddCloudTestApiItem = {
  anomaly_config?: AutomatedTestAnomalyConfig;
  config?: PipelineTaskPerformanceCloudTestConfig;
  test_type: string;
  database_name: string | null;
  schema_name: string;
  severity: number;
  model_unique_id: string;
  table_name: string;
  owners?: string[];
  tags?: string[];
  baseline_mode?: DataContractConfigApi['baseline_mode'];
  baseline_columns?: DataContractConfigApi['baseline_columns'];
  enforcement_level?: DataContractConfigApi['enforcement_level'];
};

export type CustomQueryDestination = 'cloud' | 'pr';

export type CustomSqlReferencedAsset = {
  asset_id: string;
  name: string;
  fqn: string;
};

export type CustomSqlCloudTestSchedule = AgentCustomSqlCloudSchedule;

export type ValidateCustomSqlTestApi = {
  sql: string;
  dry_run?: boolean;
  sample_limit?: number;
};

export type ValidateCustomSqlTestResult = {
  referenced_assets: CustomSqlReferencedAsset[];
  primary_asset_id: string | null;
  requires_asset_selection: boolean;
  result_columns: string[] | null;
  result_rows: Array<Record<string, unknown>> | null;
  dry_run_error: string | null;
};

export type CustomSqlCloudTestConfig = {
  fail_row_count_threshold: number;
  warn_row_count_threshold: number | null;
  store_row_sample: boolean;
  sample_row_limit: number;
  query_timeout_seconds: number;
  quality_dimension?: QualityDimension;
};

export type AddCustomSqlCloudTestApi = {
  name: string;
  description?: string;
  sql: string;
  schedule: CustomSqlCloudTestSchedule;
  primary_asset_id: string | null;
  severity: number;
  owners?: string[];
  tags?: string[];
  config?: CustomSqlCloudTestConfig;
};

export type UpdateCustomSqlConfigApi = {
  name: string;
  description?: string | null;
  sql: string;
  schedule: CustomSqlCloudTestSchedule;
  primary_asset_id: string | null;
  severity: number;
  config?: CustomSqlCloudTestConfig;
};

export type AddCloudTestApi = Array<AddCloudTestApiItem>;

export type DuplicateCloudTest = {
  test_type: string;
  database_name: string;
  schema_name: string;
  table_name: string;
};

export type CheckDuplicatesResponse = {
  duplicates: DuplicateCloudTest[];
};

export type CheckCreatableTestsApiItem = {
  asset_id?: string;
  database_name?: string;
  schema_name?: string;
  table_name?: string;
  name?: string;
  tags?: string[];
  owners?: string[];
};

export type CheckCreatableTestsApi = Array<CheckCreatableTestsApiItem>;

export type CheckCreatableItemResult = {
  creatable: boolean;
  reason?: string;
};

export type CheckCreatableResponse = {
  results: CheckCreatableItemResult[];
};

export type AddSingularTestApi = AddTestBaseApiItem & {
  path: string;
  sql_query: string;
  override_primary_test_model_id?: string;
};

export type AddTestApi = Array<AddTestBaseApiItem>;

export type EnvScheduleForm = {
  updateMethod: 'webhook' | 'hourly';
  tokenConfigType: 'configure' | 'generate';
  token: string;
};

export type SetWebhookApi = {
  token: string | null;
  enabled: boolean;
};

export type TeamUserRole = {
  id: string;
  vendorId: string;
  tenantId?: string;
  key: string;
  name: string;
  description?: string;
  isDefault: boolean;
  firstUserRole: boolean;
  createdAt: string;
  updatedAt: string;
  permissions: string[];
  level: number;
};

export type InheritedRole = {
  id: string;
  name: string;
  description?: string | null;
  builtin: boolean;
  group_names: string[];
};

export type TeamUserGroup = {
  id: string;
  roles: string[];
};

export type TeamUser = {
  id: string;
  name: string;
  profilePictureUrl: string;
  email: string;
  verified?: boolean;
  sub: string;
  phoneNumber?: string;
  provider: string;
  mfaEnrolled: boolean;
  metadata?: string;
  tenantId: string;
  roles: TeamUserRole[];
  inherited_roles?: InheritedRole[];
  permissions: {
    id: string;
    key: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    categoryId: string;
    fePermission: boolean;
  }[];
  createdAt: string;
  lastLogin?: string;
  isLocked: boolean;
  tenants: { tenantId: string; roles: TeamUserRole[] }[];
  managedBy: string;
  groups: TeamUserGroup[];
  subAccountAccessAllowed: boolean;
  activatedForTenant: boolean;
  tenantIds?: string[];
};

export type FetchTeamUsersApi = TeamUser[];

// todo: this is the old role type, use this or the new one, but dont keep both
export type Role = {
  id: string;
  name: string;
  description: string;
  level: number;
};

export type ConvertQueryApi = { sql_query: string; code_type: 'jinja' | 'sql' };

export type ConvertQueryApiResult = { converted_sql_query: string };

export type GenerateCustomSqlApi = { description: string };

export type GenerateCustomSqlResult = {
  sql: string | null;
  description: string | null;
  quality_dimension: QualityDimension | null;
  error_message: string | null;
};

export type TestTemplateMetadata = {
  test_name: string;
  docs_url?: string;
  description: string;
  quality_dimension?: string;
  popularity: number;
  supports_conditional_severity: boolean;
  coming_soon?: boolean;
};

export type TestTemplateData = {
  config_schema: object;
  config_template_yaml: string;
  test_template: TestTemplateMetadata;
};

export type AiTestConfigurationResponse = {
  yaml_config: string;
  test_description: string;
  test_name: string;
};

export type FetchAccountSettingsApi = {
  timezone: string | null;
};

export type SetAccountSettingsApi = {
  timezone: string;
};

export type Page<T> = {
  items: T[];
  page: number;
  pages: number;
  size: number;
  total: number;
};

export type Dependency = {
  direction: string;
  hops: number;
  node: {
    __typename?: string;
    displayType: AssetDisplayType;
    id: string;
    name: string;
    isCritical: boolean;
    parent: {
      id: string;
      displayType: AssetDisplayType;
      name: string;
      path: string;
      isCritical: boolean;
      latestExecution?: {
        executionTimestamp: string;
        status: string;
      };
      latestTestExecutions?: LatestTestExecution[];
      testExecutionsTotals?: Totals;
    };
    owners: string[];
    unionedOwners: string[];
    unionedTags: string[];
    tags: string[];
    path: string;
    latestExecution?: {
      executionTimestamp: string;
      status: string;
    };
    latestTestExecutions?: LatestTestExecution[];
    testExecutionsTotals?: Totals;
  };
  totals?: {
    passed: number;
    warnings: number;
    errors: number;
    failures: number;
  };
};

export type ArrayChanges = {
  added: string[] | null;
  removed: string[] | null;
};

export type EditGenericTestApi = {
  asset_id: string;
  resource_name: string;
  source_name?: string;
  column?: string;
  package?: string;
  name: string;
  tags?: ArrayChanges | null;
  owners?: ArrayChanges | null;
  description?: string | null;
  severity?: 'warn' | 'error' | 'conditional' | null;
  warn_if?: string | null;
  error_if?: string | null;
};

export type CreateOverrideAlertRuleApi = Array<{
  channel_name: string;
  alert_integration_id: string;
}>;

export type IntegrationType =
  | 'slack'
  | 'pager_duty'
  | 'opsgenie'
  | 'incident_io'
  | 'rootly'
  | 'microsoft_teams'
  | 'microsoft_teams_bot'
  | 'email';

export type AlertDestinationConditions = {
  statuses: string[];
};

export type IntegrationIssueSeverity = 'info' | 'warning' | 'error';

export type IntegrationIssue = {
  id: string;
  description: string;
  severity: IntegrationIssueSeverity;
};

export type FetchAlertIntegrationOptionsApi = {
  id: string;
  name: string;
  type: IntegrationType;
  issues: IntegrationIssue[];
}[];

export type FetchSlackChannelsApi = {
  channels: string[];
  cursor?: string;
};

export type AlertDestinationFormData =
  | {
      alertProvider: 'email';
      destination?: ValueLabelOption[];
    }
  | {
      alertProvider: Exclude<IntegrationType, 'email'>;
      destination?: ValueLabelOption;
    };

export type AlertRuleFormDestination = {
  destinationId?: string;
  integrationId: string;
  channelName: string;
  type: IntegrationType;
  configurationType?: ConfigurationType;
};

type IntegrationDestinationOptionsBase = {
  id: string;
  name: string;
  type: IntegrationType;
};

export type SlackDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'slack';
  channels: string[];
  is_list_full: boolean;
};

export type PagerDutyDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'pager_duty';
  services: Array<{ id: string; name: string }>;
};

export type MicrosoftTeamsDestinationOptions =
  IntegrationDestinationOptionsBase & {
    type: 'microsoft_teams';
    webhooks: string[];
  };

export type TeamsBotChannelOption = {
  value: string;
  label: string;
};

export type MicrosoftTeamsBotDestinationOptions =
  IntegrationDestinationOptionsBase & {
    type: 'microsoft_teams_bot';
    channel_options?: TeamsBotChannelOption[];
  };

export type OpsgenieDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'opsgenie';
};

export type IncidentIoDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'incident_io';
  sources: string[];
};

export type RootlyDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'rootly';
  sources: string[];
};

export type EmailDestinationOptions = IntegrationDestinationOptionsBase & {
  type: 'email';
  emails: string[];
};

export type FetchIntegrationDestinationOptionsApi = Array<
  | SlackDestinationOptions
  | PagerDutyDestinationOptions
  | MicrosoftTeamsDestinationOptions
  | MicrosoftTeamsBotDestinationOptions
  | OpsgenieDestinationOptions
  | IncidentIoDestinationOptions
  | RootlyDestinationOptions
  | EmailDestinationOptions
>;

export type TestManagementGqlAlertDestination = Omit<
  GqlAlertDestination,
  '__typename'
>;

export type TestManagementGqlAsset = Pick<
  GqlTableAsset,
  | 'id'
  | 'name'
  | 'dbName'
  | 'schemaName'
  | 'tableName'
  | 'materialization'
  | 'tableType'
  | 'sourceType'
  | 'sourceName'
  | 'fqn'
  | 'tags'
  | 'owners'
  | 'canCreateTests'
> & {
  cloudTestsSupported: Omit<CloudTestsSupported, '__typename'>;
};

export type TestManagementGqlAssetsResponse = {
  tableAssets: {
    items: TestManagementGqlAsset[];
  };
};

// Compact JSON scalar row returned by `tableAssetColumns`: [assetId, name, dataType]
export type TestManagementGqlAssetColumn = [
  assetId: string,
  name: string,
  dataType: string,
];

export type TestManagementGqlAssetColumnsResponse = {
  tableAssetColumns: TestManagementGqlAssetColumn[];
};

export type AssetColumnsByAssetId = Record<string, Resource['columns']>;

export type TestManagementGqlTest = Pick<
  GqlTest,
  | 'id'
  | 'name'
  | 'type'
  | 'sourceType'
  | 'description'
  | 'severity'
  | 'owners'
  | 'tags'
  | 'isSingular'
  | 'isCustomSql'
  | 'columnName'
  | 'testCategory'
  | 'namespace'
  | 'path'
  | 'updatedAt'
> & {
  // Codegen types both JSON scalars as `any`; `object` is the narrower truth.
  args: object | null;
  testMetadata: object;
  config: Pick<TestConfig, 'muted'> | null;
  // Only selected when the caller opts in.
  alertDestinations?: TestManagementGqlAlertDestination[];
  tableAsset: Pick<
    GqlTableAsset,
    | 'id'
    | 'name'
    | 'dbName'
    | 'schemaName'
    | 'tableName'
    | 'sourceName'
    | 'owners'
    | 'tags'
    | 'materialization'
    | 'lastDataModificationTime'
  > | null;
};

export type TestManagementGqlTestsResponse = {
  tests: TestManagementGqlTest[];
};

export type TestStatusGroup = {
  passed: Array<string>;
  fail: Array<string>;
  noData: Array<string>;
  warn: Array<string>;
};

export type CreateServiceNowConfigApi = {
  client_id: string;
  client_secret: string;
};

export type ServiceNowConfigApi = {
  config_key: string;
  subdomain: string;
};

export enum SourceType {
  TEST = 'test',
  MODEL = 'model',
  PIPELINE = 'pipeline',
}

export type AlertCategory =
  | 'automated_volume_anomalies'
  | 'automated_freshness_anomalies'
  | 'metric_anomalies'
  | 'pipeline_performance'
  | 'custom_sql_tests'
  | 'dbt_generic_tests'
  | 'dbt_source_freshness'
  | 'model_build'
  | 'python_sdk'
  | 'pipeline';

export type FilterType = 'is' | 'is_not' | 'contains' | 'not_contains';

export interface AlertFilter {
  property: string;
  type: FilterType;
  values: string[];
}

export type AlertFilterModel = {
  property: string;
  operator: Operator;
  values: string[];
};

export type RuleType = 'all' | 'filter';

export type EtlToolType = 'fivetran';

type IntegrationIdentifier = {
  type:
    | 'etl_tool'
    | 'alert_integration'
    | 'ticketing_tool'
    | 'messaging_integration'
    | 'incident_management_tool'
    | 'bi_tool';
  id: string;
  platform_type: EtlToolType;
};

export type IntegrationSummary = IntegrationIdentifier & {
  name: string;
  enabled: boolean;
};

export type EnvironmentIntegrations = {
  etl_tools: IntegrationSummary[];
};

type FivetranIntegrationBaseResponse = {
  integration_id: string;
  enabled: boolean;
  last_event_received_at: string | null;
  has_credentials: boolean;
};

export type FivetranM1IntegrationResponse = FivetranIntegrationBaseResponse & {
  has_credentials: false;
  webhook_url_path: string;
  shared_secret: string;
};

export type FivetranM2IntegrationResponse = FivetranIntegrationBaseResponse & {
  has_credentials: true;
  destination_group_ids: string[];
};

export type FivetranIntegrationResponse =
  | FivetranM1IntegrationResponse
  | FivetranM2IntegrationResponse;

export type CreateLegacyFivetranIntegrationResponse = {
  integration_id: string;
  webhook_url_path: string;
  shared_secret: string;
};

export type CreateFivetranIntegrationResponse = {
  integration_id: string;
  failed_webhook_group_ids: string[];
};

export type PatchFivetranIntegrationResponse = {
  integration_id: string;
  failed_webhook_group_ids: string[];
};

export type FivetranDestinationGroup = {
  id: string;
  name: string;
  region: string | null;
  is_selected: boolean;
};

export type FivetranDestinationGroups = {
  destination_groups: FivetranDestinationGroup[];
};

export type AtlanIntegrationResponse = {
  connected: boolean;
  id: string | null;
  base_url: string | null;
};

export type AtlanIntegrationForm = {
  baseUrl: string;
  apiKey?: string;
};

export type RuleFormFields = {
  ruleId?: string | null;
  name?: string;
  destinations?: AlertRuleFormDestination[];
  incidentFilters?: AlertFilterModel[];
  assetFilters?: AlertFilterModel[];
  ruleTypeIncidents?: RuleType;
  alertCategories?: AlertCategory[];
  incidentCategories?: AlertCategory[];
  cadenceType?: CadenceType;
  cadenceHour?: number;
  cadenceDay?: number;
  daysBack?: number | null;
};

export type PreDeleteAffectedDestination = {
  environment_name: string;
  channel_name: string;
  rule_name: string;
};

export type PreDeleteBlockingRule = {
  rule_id: string;
  rule_name: string;
  environment_id: string;
  environment_name: string;
};

export type PreDeleteResponse = {
  can_delete: boolean;
  affected_destinations: PreDeleteAffectedDestination[];
  blocking_rules: PreDeleteBlockingRule[];
};

export type CloudTestFeedback = {
  id: string;
  cloud_test_id: string;
  cloud_test_result_id: string | null;
  feedback_type: CloudTestFeedbackType;
  bucket_start: string;
  bucket_end: string;
  comment: string | null;
  created_by: string;
  created_at: string;
};

export type CloudTestFeedbacksResponse = {
  feedbacks: CloudTestFeedback[];
};

export type CreateFeedbackRequest = {
  cloud_test_id: string;
  feedback_type: CloudTestFeedbackType;
  cloud_test_result_id: string;
  bucket_start: string;
  comment?: string;
  override_existing?: boolean;
};

export type CreateFeedbackResult = {
  feedback: CloudTestFeedback;
  requires_test_rerun: boolean;
  superseded_feedback_ids: string[];
  other_anomalies_before_bucket: unknown[];
};

export type FeedbackErrorResponse = {
  elementary_error_code?: number;
  error_message?: string;
};
